import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import { useQuillToolbar } from './useQuillToolbar';
import 'react-quill/dist/quill.snow.css';
import './textEditor.scss';

const normalizeEmptyHtml = (html) => {
  if (!html) return null;
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = (container.textContent || container.innerText || '').replace(/\s|\u00A0/g, '');
  if (text.length === 0) {
    return null;
  }
  return html;
};

const addProtocolToLinks = (html) => {
  if (!html) return null;
  const container = document.createElement('div');
  container.innerHTML = html;

  const anchors = container.querySelectorAll('a[href]');
  anchors.forEach((a) => {
    const href = (a.getAttribute('href') || '').trim();
    if (!href) return;

    const hasProtocolOrIsSafe =
      /^(https?:)?\/\//i.test(href) ||
      href.startsWith('/') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('#');

    if (!hasProtocolOrIsSafe) {
      a.setAttribute('href', `https://${href}`);
    }
  });

  return container.innerHTML;
};

const TextEditor = ({ value = '', onChange = () => {}, toolbar }) => {
  const [editorValue, setEditorValue] = useState(value || '');
  const lastSentRef = useRef(value || '');
  const timeoutRef = useRef(null);
  const { modules, formats } = useQuillToolbar(toolbar);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const incoming = value == null ? '' : value;
    const normalizedIncoming = addProtocolToLinks(normalizeEmptyHtml(incoming));

    if (normalizedIncoming !== editorValue) {
      setEditorValue(normalizedIncoming);
    }
    lastSentRef.current = normalizedIncoming;
  }, [value]);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const root = wrapperRef.current;
    const addHelper = (tooltip) => {
      const linkInput = tooltip.querySelector('input[data-link]');
      if (!linkInput) return;
      linkInput.placeholder = 'https://example.com o /ruta';
      if (!tooltip.querySelector('.link-helper-info')) {
        const info = document.createElement('div');
        info.className = 'link-helper-info';
        info.innerHTML = `
        <b>For external links</b> use <b>https://</b><br/>
        <b>For internal paths</b> you can use <b>/route</b>
      `;
        linkInput.parentNode.appendChild(info);
      }
    };

    const removeHelper = () => {
      const existing = root.querySelector('.link-helper-info');
      if (existing) existing.remove();
    };

    const checkTooltip = () => {
      const tooltip = root.querySelector('.ql-tooltip.ql-editing');
      if (tooltip) {
        addHelper(tooltip);
      } else {
        removeHelper();
      }
    };

    const observer = new MutationObserver(checkTooltip);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    checkTooltip();

    return () => observer.disconnect();
  }, []);


  const debouncedSave = (data) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      onChange({ target: { value: data } });
    }, 500);
  };

  const handleEditorChange = (content) => {
    const raw = content || '';
    const normalized = addProtocolToLinks(normalizeEmptyHtml(raw));
    setEditorValue(normalized);
    if (normalized !== lastSentRef.current) {
      lastSentRef.current = normalized;
      debouncedSave(normalized);
    }
  };
  
  return (
    <div className="text-editor-wrapper" ref={wrapperRef}>
      <ReactQuill
        value={editorValue}
        onChange={handleEditorChange}
        modules={modules}
        formats={formats}
        theme="snow"
      />
    </div>
  );
};

TextEditor.propTypes = {
  value: PropTypes.node,
  onChange: PropTypes.func,
  toolbar: PropTypes.array,
};

TextEditor.defaultProps = {
  value: null,
  onChange: () => {},
  toolbar: ['bold', 'italic'],
};

export default TextEditor;
